/**
 * @file Defines a directive for spreading properties onto an element.
 *
 * This directive is specifically designed for the component loader to dynamically
 * apply attributes and properties to custom elements without hardcoding all possible attributes.
 */
import { directive, Directive } from 'lit/directive.js';
import { PartType } from 'lit/directive.js';
/**
 * Creates a reusable Lit directive that dynamically applies attributes and properties
 * to a custom element. This directive is specifically designed for the component loader.
 *
 * @param allowedProps - A Set of property names explicitly allowed for binding.
 *                       The directive will also always allow:
 *                       - `data-*` attributes
 *                       - `aria-*` attributes
 * @return A directive instance that can be used like:
 *          `<my-element ${spreadProps(allowedProps)({...})}></my-element>`
 *
 * ### Supported Features:
 * - `.property` and attribute reflection for all allowed props
 * - Pass-through for `data-*` and `aria-*` attributes (no whitelist needed)
 * - Kebab-case conversion for standard camelCase property names
 * - Boolean attribute handling (e.g. `disabled`)
 */
export function spreadProps(allowedProps) {
    var _a;
    return directive((_a = class extends Directive {
            /**
             * Satisfies Lit's abstract base class requirement.
             * This directive does not use the `render` method.
             * @param _props
             */
            render(_props) {
                return '';
            }
            /**
             * Called when the directive is updated. Applies props and attributes
             * to the target element.
             * @param part
             * @param root0
             * @param root0."0"
             */
            update(part, [props]) {
                const el = part.element;
                // Ensure we're working with a plain object
                if (typeof props !== 'object' || props === null) {
                    return '';
                }
                for (const [key, value] of Object.entries(props)) {
                    const isDataAttr = key.startsWith('data-');
                    const isAriaAttr = key.startsWith('aria-');
                    const isAllowed = allowedProps.has(key) || isDataAttr || isAriaAttr;
                    if (!isAllowed) {
                        continue;
                    }
                    // Determine final attribute name
                    const attrName = (isDataAttr || isAriaAttr)
                        ? key
                        : this.toKebabCase(key);
                    // Boolean values: set/remove attribute and set property
                    if (typeof value === 'boolean') {
                        if (value) {
                            el.setAttribute(attrName, '');
                            el[key] = true;
                        }
                        else {
                            el.removeAttribute(attrName);
                            el[key] = false;
                        }
                    }
                    // Other values: apply both property and attribute
                    else if (value != null) {
                        el[key] = value;
                        el.setAttribute(attrName, String(value));
                    }
                }
                return '';
            }
            /**
             * Converts a camelCase string to kebab-case for HTML attribute names.
             * Example: `needsConfirmation` → `needs-confirmation`
             * @param str
             */
            toKebabCase(str) {
                return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
            }
        },
        /**
         * Specifies this directive only applies to elements, not text or attribute parts.
         */
        _a.partType = PartType.ELEMENT,
        _a));
}
